/**
 * Capa de Acceso a Datos (DL) para la Gestión de Operadores y Sesiones
 * Endpoints: /api/Authentication/...
 */

const getApiBaseUrl = () => {
    const envUrl = process.env.REACT_APP_API_URL;
    if (envUrl) {
        return envUrl.endsWith('/') ? envUrl : `${envUrl}/`;
    }
    return '/api/';
};

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    const headers = {
        'Content-Type': 'application/json'
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

/**
 * Obtiene la lista completa de operadores registrados y el conteo de sesiones activas.
 * GET /api/Authentication/operators
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
const GetOperators = async () => {
    try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}Authentication/operators`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage = 'No fue posible obtener la lista de operadores.';
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch {
                // Ignore JSON parse error
            }
            return { success: false, message: errorMessage };
        }

        const data = await response.json();
        return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red o conexión al consultar los operadores.'
        };
    }
};

/**
 * Registra un nuevo operador en el sistema.
 * POST /api/Authentication/register-operator
 * @param {Object} operatorData - { username, password, role, brandId }
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const RegisterOperator = async (operatorData) => {
    try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}Authentication/register-operator`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(operatorData)
        });

        if (!response.ok) {
            let errorMessage = 'Error al registrar el operador.';
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch {
                // Ignore JSON parse error
            }
            return { success: false, message: errorMessage };
        }

        const data = await response.json();
        return { success: true, data: data };
    } catch (error) {
        return {
            success: false,
            message: 'Error de conexión al registrar el operador.'
        };
    }
};

/**
 * Actualiza la información de un operador (rol, contraseña y/o brandId).
 * PUT /api/Authentication/operators/{id}
 * @param {string} id - Guid del operador
 * @param {Object} operatorData - { role, password, brandId }
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const UpdateOperator = async (id, operatorData) => {
    try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}Authentication/operators/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(operatorData)
        });

        if (!response.ok) {
            let errorMessage = 'Error al actualizar el operador.';
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch {
                // Ignore JSON parse error
            }
            return { success: false, message: errorMessage };
        }

        const data = await response.json();
        return { success: true, data: data };
    } catch (error) {
        return {
            success: false,
            message: 'Error de conexión al actualizar el operador.'
        };
    }
};

/**
 * Elimina permanentemente a un operador del sistema.
 * DELETE /api/Authentication/operators/{id}
 * @param {string} id - Guid del operador
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const DeleteOperator = async (id) => {
    try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}Authentication/operators/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage = 'Error al eliminar el operador.';
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch {
                // Ignore JSON parse error
            }
            return { success: false, message: errorMessage };
        }

        let data = null;
        try {
            data = await response.json();
        } catch {
            data = { message: 'Operador eliminado exitosamente' };
        }

        return {
            success: true,
            data: data,
            message: data?.message || 'Operador eliminado exitosamente'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de conexión al eliminar el operador.'
        };
    }
};

/**
 * Fuerza el cierre de todas las sesiones activas (refresh tokens) de un operador.
 * POST /api/Authentication/operators/{id}/terminate-sessions
 * @param {string} id - Guid del operador
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
const TerminateOperatorSessions = async (id) => {
    try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}Authentication/operators/${id}/terminate-sessions`, {
            method: 'POST',
            headers: getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage = 'Error al terminar las sesiones del operador.';
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch {
                // Ignore JSON parse error
            }
            return { success: false, message: errorMessage };
        }

        const data = await response.json();
        return {
            success: true,
            data: data,
            message: data?.message || 'Sesiones terminadas exitosamente'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de conexión al terminar las sesiones.'
        };
    }
};

/**
 * Cambia la contraseña del operador actualmente autenticado.
 * POST /api/Authentication/change-password
 * @param {Object} passwordData - { currentPassword, newPassword }
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const ChangePassword = async (passwordData) => {
    try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}Authentication/change-password`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(passwordData)
        });

        if (!response.ok) {
            let errorMessage = 'Error al cambiar la contraseña.';
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch {
                // Ignore JSON parse error
            }
            return { success: false, message: errorMessage };
        }

        let data = null;
        try {
            data = await response.json();
        } catch {
            data = { message: 'Contraseña actualizada exitosamente' };
        }

        return {
            success: true,
            data: data,
            message: data?.message || 'Contraseña actualizada exitosamente'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de conexión al cambiar la contraseña.'
        };
    }
};

export {
    GetOperators,
    RegisterOperator,
    UpdateOperator,
    DeleteOperator,
    TerminateOperatorSessions,
    ChangePassword
};
